import './styles.scss';

import { faArrowRotateRight, faCheck, faCopy, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import TableGrid from '../../../components/table-grid';
import { ApiKey } from '../../../providers/api-keys/api-keys-service';
import { useApiKeys } from '../../../providers/api-keys/use-api-keys';

import CreateApiKeyModal from './create-api-key-modal';
import DeleteApiKeyModal from './delete-api-key-modal';
import { messageForError } from './error-message';
import { formatUtc } from './format-utc';
import { labelForPermission } from './permission-labels';

const COPY_FEEDBACK_MS = 1500;

// The uuid is the client identifier a user needs to connect (uuid + secret).
// The PEM public_key is not something a user pastes anywhere, so it's kept out
// of the table to stop it blowing out the row width.
const CopyableId = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  // A row can unmount inside the feedback window (e.g. Delete + list refresh);
  // hold the timer id so we can clear it on unmount and before re-arming, else
  // setCopied fires on an unmounted component.
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch {
      // clipboard blocked — leave the id visible for manual copy
    }
  }, [value]);

  return (
    <div className="client-id-cell">
      <span className="client-id-value" title={value}>
        {value}
      </span>
      <Tooltip title={copied ? 'Copied' : 'Copy client ID'}>
        <button
          type="button"
          className="client-id-copy"
          onClick={handleCopy}
          aria-label="Copy client ID"
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
      </Tooltip>
    </div>
  );
};

const ApiKeys = () => {
  const { listApiKeys, createApiKey, deleteApiKey } = useApiKeys();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadApiKeys = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (error) {
      setLoadError(messageForError(error, 'Failed to load API keys.'));
      setApiKeys([]);
    } finally {
      setIsLoading(false);
    }
  }, [listApiKeys]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleOpenCreate = () => setOpenCreateModal(true);
  const handleCloseCreate = useCallback(() => setOpenCreateModal(false), []);

  const handleOpenDelete = useCallback((apiKey: ApiKey) => {
    setDeleteTarget(apiKey);
    setDeleteError(null);
    setOpenDeleteModal(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setOpenDeleteModal(false);
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteApiKey(deleteTarget.uuid);
      handleCloseDelete();
      await loadApiKeys();
    } catch (error) {
      setDeleteError(messageForError(error, 'Failed to delete API key.'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteApiKey, deleteTarget, handleCloseDelete, loadApiKeys]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        sortable: false,
        minWidth: 120,
        flex: 1,
      },
      {
        field: 'uuid',
        headerName: 'Client ID',
        sortable: false,
        minWidth: 300,
        flex: 1.4,
        cellRenderer: ({ data }: { data: ApiKey }) => <CopyableId value={data?.uuid ?? ''} />,
      },
      {
        field: 'permissions',
        headerName: 'Permissions',
        sortable: false,
        minWidth: 240,
        flex: 2,
        autoHeight: true,
        wrapText: true,
        cellRenderer: ({ data }: { data: ApiKey }) => (
          <div className="permissions-cell">
            {(data?.permissions ?? []).map((permission) => (
              <span className="permission-chip" key={permission}>
                {labelForPermission(permission)}
              </span>
            ))}
          </div>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        sortable: false,
        minWidth: 90,
        flex: 0.7,
      },
      {
        field: 'created_at',
        headerName: 'Created',
        sortable: false,
        minWidth: 180,
        flex: 1,
        cellRenderer: ({ data }: { data: ApiKey }) => <span>{formatUtc(data?.created_at)}</span>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        minWidth: 180,
        flex: 1,
        cellRenderer: ({ data }: { data: ApiKey }) => (
          <div className="actions-container">
            <Button className="delete-action-button" onClick={() => handleOpenDelete(data)}>
              Delete
            </Button>
            {/*
              TODO(IM-2168): Regenerate mints a new key and returns a fresh one-time
              secret (the backend never re-serves an existing secret). Wire it to
              RevealSecret when the endpoint lands; disabled placeholder for now.
            */}
            <Tooltip title="Regenerate is coming soon">
              <span>
                <Button className="regenerate-action-button" disabled>
                  Regenerate
                </Button>
              </span>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleOpenDelete],
  );

  return (
    <div className="api-keys-container">
      <h1 className="title">API Keys</h1>

      <div className="buttons">
        <Button className="button" onClick={loadApiKeys}>
          <FontAwesomeIcon icon={faArrowRotateRight} />
          <span>Refresh</span>
        </Button>
        <Button className="button button-filled" onClick={handleOpenCreate}>
          <FontAwesomeIcon icon={faPlus} />
          <span>Add new API key</span>
        </Button>
      </div>

      {loadError && <div className="api-keys-error">{loadError}</div>}

      <div className="api-keys">
        <TableGrid
          columns={columns}
          rowData={apiKeys}
          className="api-keys-table-grid"
          isLoading={isLoading}
          noWatermark
          autoHeight
        />
      </div>

      <CreateApiKeyModal
        open={openCreateModal}
        handleClose={handleCloseCreate}
        createApiKey={createApiKey}
        onCreated={loadApiKeys}
      />

      <DeleteApiKeyModal
        open={openDeleteModal}
        handleClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        apiKeyLabel={deleteTarget?.name || deleteTarget?.public_key}
        isLoading={isDeleting}
        errorMessage={deleteError}
      />
    </div>
  );
};

export default ApiKeys;
